import { EventVenueType } from '@core/enums/event/event-venue-type.enum';
import { OnlineEventType } from '@core/enums/event/online-event-type.enum';
import { IEventBase } from '@core/interfaces/event/event-base.interface';
import { IPriceCategory } from '@core/interfaces/product/pricing-category.interface';
import { AAGUID, ISODateTime, parseArray } from '@interticket/core';
import { DateTime } from 'luxon';
import { PriceCategory } from '../product/price-category.model';
import { TicketTemplateFormats } from '../ticket-template/ticket-template-formats.model';

export class EventBase implements IEventBase {

  readonly availableCapacity: number;
  readonly availableFrom: ISODateTime;
  readonly availableTo: ISODateTime;
  readonly canBeSold: boolean;
  readonly comment: string;
  readonly endDate: ISODateTime;
  readonly id: AAGUID;
  readonly isAvailable: boolean;
  readonly isBeforeSale: boolean;
  readonly isDateHidden: boolean;
  readonly isEnabledStreaming: boolean;
  readonly isFree: boolean;
  readonly isFreeEntry: boolean;
  readonly isSoldout: boolean;
  readonly livestreamEndDate: ISODateTime;
  readonly maxCapacity: number;
  readonly name: string;
  readonly onGoing: boolean;
  readonly onlineEventType: OnlineEventType;
  readonly previewImageId: AAGUID;
  readonly priceCategories: PriceCategory[];
  readonly shortDescription: string;
  readonly soldTicketsCount: number;
  readonly startDate: ISODateTime;
  readonly ticketFormats: TicketTemplateFormats;
  readonly type: EventVenueType;
  readonly customersCountryIsEnabled: boolean;
  readonly currentStreamType: OnlineEventType;

  constructor(iEventBase: IEventBase) {
    this.availableCapacity = iEventBase.availableCapacity;
    this.availableFrom = iEventBase.availableFrom;
    this.availableTo = iEventBase.availableTo;
    this.comment = iEventBase.comment;
    this.endDate = iEventBase.endDate;
    this.id = iEventBase.id;
    this.isFree = !!iEventBase.isFree;
    this.isFreeEntry = !!iEventBase.isFreeEntry;
    this.livestreamEndDate = iEventBase.livestreamEndDate;
    this.maxCapacity = iEventBase.maxCapacity;
    this.name = iEventBase.name;
    this.onlineEventType = iEventBase.onlineEventType;
    this.previewImageId = iEventBase.previewImageId;
    this.priceCategories = parseArray<IPriceCategory, PriceCategory>(iEventBase.priceCategories, PriceCategory);
    this.shortDescription = iEventBase.shortDescription;
    this.soldTicketsCount = iEventBase.soldTicketsCount;
    this.startDate = iEventBase.startDate;
    this.ticketFormats = iEventBase.ticketFormats ? new TicketTemplateFormats(iEventBase.ticketFormats) : null;
    this.type = iEventBase.type;
    this.customersCountryIsEnabled = !!iEventBase.customersCountryIsEnabled;

    const now: DateTime = DateTime.local();
    this.isSoldout = this.getIsSoldout();
    this.onGoing = this.getOnGoing(now);
    this.isAvailable = this.getIsAvailable(now);
    this.canBeSold = this.getCanBeSold();
    this.isEnabledStreaming = this.getIsEnabledStreaming(now);
    this.isBeforeSale = this.getIsBeforeSale(now);
    this.isDateHidden = this.getIsDateHidden(now);
    this.currentStreamType = this.getCurrentStreamType(now);
  }

  private getIsSoldout(): boolean {
    return !this.isFree && (this.availableCapacity === 0 || this.getIsSoldoutAllPriceCategories());
  }

  private getIsSoldoutAllPriceCategories(): boolean {
    return this.priceCategories.every(priceCategory => priceCategory.availableCapacity === 0);
  }

  private getOnGoing(now: DateTime): boolean {
    return DateTime.fromISO(this.startDate) <= now && DateTime.fromISO(this.endDate) >= now;
  }

  private getIsAvailable(now: DateTime): boolean {
    return DateTime.fromISO(this.availableFrom) <= now && DateTime.fromISO(this.availableTo) >= now;
  }

  private getCanBeSold(): boolean {
    return !this.isFree && !this.isSoldout && this.isAvailable;
  }

  private getIsEnabledStreaming(now: DateTime): boolean {
    return this.type === EventVenueType.ONLINE
      && DateTime.fromISO(this.startDate) <= now
      && DateTime.fromISO(this.endDate).plus({ minutes: 30 }) >= now;
  }

  private getIsBeforeSale(now: DateTime): boolean {
    return now < DateTime.fromISO(this.availableFrom);
  }

  private getIsDateHidden(now: DateTime): boolean {
    switch (this.onlineEventType) {
      case OnlineEventType.LIVESTREAM_VIDEO:
        return DateTime.fromISO(this.livestreamEndDate) < now;

      case OnlineEventType.VIDEO:
        return DateTime.fromISO(this.startDate) < now;

      default:
        return false;
    }
  }

  private getCurrentStreamType(now: DateTime): OnlineEventType {
    if (this.onlineEventType === OnlineEventType.LIVESTREAM_VIDEO) {
      return DateTime.fromISO(this.livestreamEndDate) >= now ? OnlineEventType.LIVESTREAM : OnlineEventType.VIDEO;
    }
    return this.onlineEventType;
  }

}
